import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Link2, X as XIcon, MessageCircle, Check, Mail } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface ShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  url: string;
  /** Optional message shown in a preview card above the share buttons */
  message?: string;
}

export default function ShareDialog({ open, onOpenChange, title, url, message }: ShareDialogProps) {
  const [copied, setCopied] = useState(false);

  const copyLink = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      toast({ title: "Link copied!" });
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      toast({ title: "Link", description: url });
    });
  };

  const encodedUrl = encodeURIComponent(url);
  const encodedText = encodeURIComponent(message || title);

  const shareToX = () => window.open(`https://x.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`, "_blank");
  const shareToWhatsApp = () => window.open(`https://wa.me/?text=${encodedText}%20${encodedUrl}`, "_blank");
  const shareToSms = () => window.open(`sms:?&body=${encodedText}%20${encodedUrl}`, "_blank");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {message && (
          <div className="rounded-lg border border-border bg-muted/50 p-4 text-sm text-foreground">
            {message}
          </div>
        )}

        <div className="flex items-center justify-center gap-6 py-4">
          <ShareButton label="Copy link" onClick={copyLink}>
            {copied ? <Check className="h-5 w-5" /> : <Link2 className="h-5 w-5" />}
          </ShareButton>
          <ShareButton label="X" onClick={shareToX}>
            <XIcon className="h-5 w-5" />
          </ShareButton>
          <ShareButton label="WhatsApp" onClick={shareToWhatsApp}>
            <MessageCircle className="h-5 w-5" />
          </ShareButton>
          <ShareButton label="Messages" onClick={shareToSms}>
            <Mail className="h-5 w-5" />
          </ShareButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ShareButton({ children, label, onClick }: { children: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center gap-1.5 group"
    >
      <span className="flex h-12 w-12 items-center justify-center rounded-full border border-border bg-background text-foreground transition-colors group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary">
        {children}
      </span>
      <span className="text-xs text-muted-foreground">{label}</span>
    </button>
  );
}
